// Users Service for East End Prep Sports Admin
// Handles user management API calls

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

class UsersService {
  // Get authentication headers
  static getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  // Get individual user by ID
  static async getUserById(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user:', error);
      // Return mock data for development
      const mockUsers = this.getMockUsers().users;
      const user = mockUsers.find(u => u.id === parseInt(userId));
      return {
        success: true,
        user: user || null
      };
    }
  }

  // Get all users with pagination and filters
  static async getUsers(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 50,
        search: params.search || '',
        role: params.role || '',
        status: params.status || '',
        sortBy: params.sortBy || 'created_at',
        sortOrder: params.sortOrder || 'desc'
      });

      const response = await fetch(`${API_BASE_URL}/admin/users?${queryParams}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching users:', error);
      // Return mock data for development
      return this.getMockUsers(params);
    }
  }

  // Create a new user
  static async createUser(userData) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message || `Failed to create user: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  // Update an existing user
  static async updateUser(userId, userData) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update user');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating user:', error);
      // For development, simulate successful update
      return {
        success: true,
        user: {
          id: userId,
          ...userData,
          updated_at: new Date().toISOString()
        }
      };
    }
  }

  // Delete a user
  static async deleteUser(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete user');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting user:', error);
      // For development, simulate successful deletion
      return { success: true, message: 'User deleted successfully' };
    }
  }

  // Update user status (activate/deactivate)
  static async updateUserStatus(userId, isActive) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ isActive })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update user status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating user status:', error);
      // For development, simulate successful status update
      return {
        success: true,
        message: `User ${isActive ? 'activated' : 'deactivated'} successfully`
      };
    }
  }

  // Reset user password
  static async resetUserPassword(userId, newPassword) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ password: newPassword })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reset password');
      }

      return await response.json();
    } catch (error) {
      console.error('Error resetting password:', error);
      // For development, simulate successful password reset
      return { success: true, message: 'Password reset successfully' };
    }
  }

  // Get user statistics
  static async getUserStats() {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/stats`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user statistics');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user stats:', error);
      // Return mock stats for development
      return {
        totalUsers: 25,
        activeUsers: 22,
        adminUsers: 3,
        recentLogins: 8,
        usersByRole: {
          admin: 3,
          editor: 5,
          coach: 8,
          moderator: 4,
          viewer: 5
        }
      };
    }
  }

  // Mock data for development
  static getMockUsers(params = {}) {
    const mockUsers = [
      {
        id: 1,
        first_name: 'John',
        last_name: 'Smith',
        email: 'john.smith@eastendprep.edu',
        role: 'admin',
        is_active: true,
        phone: '(555) 123-4567',
        bio: 'Athletic Director and Head Coach',
        avatar_url: '/images/avatars/john-smith.jpg',
        last_login: '2024-01-15T14:30:00Z',
        login_count: 245,
        permissions: ['manage_users', 'manage_sports', 'manage_news', 'manage_events', 'manage_media'],
        created_at: '2023-08-15T10:00:00Z',
        updated_at: '2024-01-15T14:30:00Z'
      },
      {
        id: 2,
        first_name: 'Sarah',
        last_name: 'Johnson',
        email: 'sarah.johnson@eastendprep.edu',
        role: 'editor',
        is_active: true,
        phone: '(555) 234-5678',
        bio: 'Sports Information Director',
        avatar_url: '/images/avatars/sarah-johnson.jpg',
        last_login: '2024-01-14T16:45:00Z',
        login_count: 156,
        permissions: ['manage_news', 'manage_events', 'manage_media'],
        created_at: '2023-09-01T09:00:00Z',
        updated_at: '2024-01-14T16:45:00Z'
      },
      {
        id: 3,
        first_name: 'Mike',
        last_name: 'Davis',
        email: 'mike.davis@eastendprep.edu',
        role: 'coach',
        is_active: true,
        phone: '(555) 345-6789',
        bio: 'Head Basketball Coach',
        avatar_url: '/images/avatars/mike-davis.jpg',
        last_login: '2024-01-13T12:20:00Z',
        login_count: 89,
        permissions: ['manage_sports'],
        created_at: '2023-10-15T11:00:00Z',
        updated_at: '2024-01-13T12:20:00Z'
      },
      {
        id: 4,
        first_name: 'Emily',
        last_name: 'Wilson',
        email: 'emily.wilson@eastendprep.edu',
        role: 'viewer',
        is_active: false,
        phone: '(555) 456-7890',
        bio: 'Former Assistant Coach',
        avatar_url: '/images/avatars/emily-wilson.jpg',
        last_login: '2023-12-20T08:15:00Z',
        login_count: 34,
        permissions: [],
        created_at: '2023-11-01T14:00:00Z',
        updated_at: '2023-12-20T08:15:00Z'
      },
      {
        id: 5,
        first_name: 'David',
        last_name: 'Brown',
        email: 'david.brown@eastendprep.edu',
        role: 'moderator',
        is_active: true,
        phone: '(555) 567-8901',
        bio: 'Communications Manager',
        avatar_url: '/images/avatars/david-brown.jpg',
        last_login: '2024-01-15T09:30:00Z',
        login_count: 78,
        permissions: ['manage_news', 'manage_events'],
        created_at: '2023-12-01T13:00:00Z',
        updated_at: '2024-01-15T09:30:00Z'
      }
    ];

    // Apply filters if provided
    let filteredUsers = mockUsers;
    
    if (params.search) {
      const searchTerm = params.search.toLowerCase();
      filteredUsers = filteredUsers.filter(user => 
        user.first_name.toLowerCase().includes(searchTerm) ||
        user.last_name.toLowerCase().includes(searchTerm) ||
        user.email.toLowerCase().includes(searchTerm)
      );
    }

    if (params.role) {
      filteredUsers = filteredUsers.filter(user => user.role === params.role);
    }

    if (params.status) {
      const isActive = params.status === 'active';
      filteredUsers = filteredUsers.filter(user => user.is_active === isActive);
    }

    return {
      users: filteredUsers,
      pagination: {
        currentPage: parseInt(params.page) || 1,
        totalPages: 1,
        totalItems: filteredUsers.length,
        itemsPerPage: parseInt(params.limit) || 50
      }
    };
  }

  // User roles and permissions
  static getRoles() {
    return [
      { 
        value: 'admin', 
        label: 'Administrator', 
        description: 'Full system access',
        permissions: ['manage_users', 'manage_sports', 'manage_news', 'manage_events', 'manage_media', 'system_settings']
      },
      { 
        value: 'editor', 
        label: 'Editor', 
        description: 'Content management access',
        permissions: ['manage_news', 'manage_events', 'manage_media']
      },
      { 
        value: 'moderator', 
        label: 'Moderator', 
        description: 'Limited content access',
        permissions: ['manage_news', 'manage_events']
      },
      { 
        value: 'coach', 
        label: 'Coach', 
        description: 'Sports management access',
        permissions: ['manage_sports']
      },
      { 
        value: 'viewer', 
        label: 'Viewer', 
        description: 'Read-only access',
        permissions: []
      }
    ];
  }

  static getAllPermissions() {
    return [
      { value: 'manage_users', label: 'Manage Users' },
      { value: 'manage_sports', label: 'Manage Sports' },
      { value: 'manage_news', label: 'Manage News' },
      { value: 'manage_events', label: 'Manage Events' },
      { value: 'manage_media', label: 'Manage Media' },
      { value: 'system_settings', label: 'System Settings' }
    ];
  }

  static getRolePermissions(role) {
    const roles = this.getRoles();
    const roleData = roles.find(r => r.value === role);
    return roleData ? roleData.permissions : [];
  }
}

export default UsersService;